// src/components/trades/mockups/TradeProposalMockupViewer.js
// MOCKUP VIEWER - View all 3 design options

import React, { useState } from 'react';
import { dynastyTheme } from '../../../services/colorService';
import TradeProposalMockupA from './TradeProposalMockupA';
import TradeProposalMockupB from './TradeProposalMockupB';
import TradeProposalMockupC from './TradeProposalMockupC';

/**
 * Trade Proposal Mockup Viewer
 * Shows all 3 design options for comparison
 */
const TradeProposalMockupViewer = () => {
  const [activeOption, setActiveOption] = useState('A');

  return (
    <div className={`min-h-screen ${dynastyTheme.classes.bg.dark} p-6`}>
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="text-center space-y-2">
          <h1 className={`text-3xl font-bold ${dynastyTheme.classes.text.primary}`}>
            Trade Proposal Interface - Design Options
          </h1>
          <p className={`${dynastyTheme.classes.text.neutralLight}`}>
            Choose your favorite layout for the trade proposal interface
          </p>
        </div>

        {/* Option Selector */}
        <div className={`${dynastyTheme.components.card.base} p-4`}>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Option A Card */}
            <button
              onClick={() => setActiveOption('A')}
              className={`
                p-4 rounded-lg border-2 text-left transition-all
                ${activeOption === 'A' 
                  ? `${dynastyTheme.classes.border.primary} ${dynastyTheme.classes.bg.primaryLight}`
                  : `${dynastyTheme.classes.border.neutral} ${dynastyTheme.classes.bg.darkLighter}`
                }
              `}
            >
              <div className={`font-bold ${dynastyTheme.classes.text.white} mb-2`}>
                Option A: Split-Screen
              </div>
              <div className={`text-sm ${dynastyTheme.classes.text.neutralLight} mb-3`}>
                Traditional side-by-side layout. Best for desktop users who want to see both rosters simultaneously.
              </div>
              <div className="space-y-1">
                <div className={`text-xs ${dynastyTheme.classes.text.success}`}>
                  ✓ See both teams at once
                </div>
                <div className={`text-xs ${dynastyTheme.classes.text.success}`}>
                  ✓ Easy comparison
                </div>
                <div className={`text-xs ${dynastyTheme.classes.text.error}`}>
                  ✗ Requires wide screen
                </div>
              </div>
            </button>

            {/* Option B Card */}
            <button
              onClick={() => setActiveOption('B')}
              className={`
                p-4 rounded-lg border-2 text-left transition-all
                ${activeOption === 'B' 
                  ? `${dynastyTheme.classes.border.primary} ${dynastyTheme.classes.bg.primaryLight}`
                  : `${dynastyTheme.classes.border.neutral} ${dynastyTheme.classes.bg.darkLighter}`
                }
              `}
            >
              <div className={`font-bold ${dynastyTheme.classes.text.white} mb-2`}>
                Option B: Tabbed Mobile-First
              </div>
              <div className={`text-sm ${dynastyTheme.classes.text.neutralLight} mb-3`}>
                Switch between tabs. Best for mobile users and smaller screens with sticky trade summary.
              </div>
              <div className="space-y-1">
                <div className={`text-xs ${dynastyTheme.classes.text.success}`}>
                  ✓ Mobile-friendly
                </div>
                <div className={`text-xs ${dynastyTheme.classes.text.success}`}>
                  ✓ Less overwhelming
                </div>
                <div className={`text-xs ${dynastyTheme.classes.text.error}`}>
                  ✗ Can't compare side-by-side
                </div>
              </div>
            </button>

            {/* Option C Card */}
            <button
              onClick={() => setActiveOption('C')}
              className={`
                p-4 rounded-lg border-2 text-left transition-all
                ${activeOption === 'C' 
                  ? `${dynastyTheme.classes.border.primary} ${dynastyTheme.classes.bg.primaryLight}`
                  : `${dynastyTheme.classes.border.neutral} ${dynastyTheme.classes.bg.darkLighter}`
                }
              `}
            >
              <div className={`font-bold ${dynastyTheme.classes.text.white} mb-2`}>
                Option C: Center Trade Bar
              </div>
              <div className={`text-sm ${dynastyTheme.classes.text.neutralLight} mb-3`}>
                Central staging area with compact rosters. Visual trade flow with drag-like feel.
              </div>
              <div className="space-y-1">
                <div className={`text-xs ${dynastyTheme.classes.text.success}`}>
                  ✓ Clear trade visualization
                </div>
                <div className={`text-xs ${dynastyTheme.classes.text.success}`}>
                  ✓ Unique UX
                </div>
                <div className={`text-xs ${dynastyTheme.classes.text.error}`}>
                  ✗ Requires understanding
                </div>
              </div>
            </button>
          </div>
        </div>

        {/* Active Mockup Display */}
        <div>
          {activeOption === 'A' && <TradeProposalMockupA />}
          {activeOption === 'B' && <TradeProposalMockupB />}
          {activeOption === 'C' && <TradeProposalMockupC />}
        </div>

        {/* Feature Comparison Table */}
        <div className={`${dynastyTheme.components.card.base} p-6`}>
          <h3 className={`text-xl font-bold ${dynastyTheme.classes.text.white} mb-4`}>
            Feature Comparison
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className={`border-b ${dynastyTheme.classes.border.neutral}`}>
                  <th className={`text-left p-3 ${dynastyTheme.classes.text.white}`}>Feature</th>
                  <th className={`text-center p-3 ${dynastyTheme.classes.text.white}`}>Option A</th>
                  <th className={`text-center p-3 ${dynastyTheme.classes.text.white}`}>Option B</th>
                  <th className={`text-center p-3 ${dynastyTheme.classes.text.white}`}>Option C</th>
                </tr>
              </thead>
              <tbody>
                <ComparisonRow
                  feature="Mobile Friendly"
                  a="⚠️"
                  b="✅"
                  c="⚠️"
                />
                <ComparisonRow
                  feature="Desktop Optimized"
                  a="✅"
                  b="⚠️"
                  c="✅"
                />
                <ComparisonRow
                  feature="Side-by-side Comparison"
                  a="✅"
                  b="❌"
                  c="✅"
                />
                <ComparisonRow
                  feature="Visual Trade Flow"
                  a="⚠️"
                  b="✅"
                  c="✅"
                />
                <ComparisonRow
                  feature="Quick Selection"
                  a="✅"
                  b="✅"
                  c="⚠️"
                />
                <ComparisonRow
                  feature="Screen Space Required"
                  a="Large"
                  b="Small"
                  c="Medium"
                />
                <ComparisonRow
                  feature="Learning Curve"
                  a="Low"
                  b="Low"
                  c="Medium"
                />
              </tbody>
            </table>
          </div>
        </div>

        {/* Instructions */}
        <div className={`${dynastyTheme.components.card.base} p-6`}>
          <h3 className={`text-xl font-bold ${dynastyTheme.classes.text.white} mb-4`}>
            How to Decide
          </h3>
          <div className="space-y-4">
            <div>
              <h4 className={`font-bold ${dynastyTheme.classes.text.primary} mb-2`}>
                Choose Option A if:
              </h4>
              <ul className={`list-disc list-inside ${dynastyTheme.classes.text.neutralLight} space-y-1`}>
                <li>Your users primarily use desktop/laptop computers</li>
                <li>You want a familiar, traditional fantasy sports interface</li>
                <li>Direct comparison of rosters is important</li>
                <li>You have wide screens available</li>
              </ul>
            </div>

            <div>
              <h4 className={`font-bold ${dynastyTheme.classes.text.primary} mb-2`}>
                Choose Option B if:
              </h4>
              <ul className={`list-disc list-inside ${dynastyTheme.classes.text.neutralLight} space-y-1`}>
                <li>Your users frequently trade on mobile devices</li>
                <li>You want the simplest, most intuitive interface</li>
                <li>Screen real estate is limited</li>
                <li>You prefer focused, less cluttered UX</li>
              </ul>
            </div>

            <div>
              <h4 className={`font-bold ${dynastyTheme.classes.text.primary} mb-2`}>
                Choose Option C if:
              </h4>
              <ul className={`list-disc list-inside ${dynastyTheme.classes.text.neutralLight} space-y-1`}>
                <li>You want a unique, modern trade experience</li>
                <li>Visual trade flow representation is valuable</li>
                <li>You want to differentiate from competitors</li>
                <li>Your users are tech-savvy dynasty players</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Decision Prompt */}
        <div className={`${dynastyTheme.components.card.base} p-6 text-center`}>
          <h3 className={`text-xl font-bold ${dynastyTheme.classes.text.white} mb-4`}>
            Ready to Decide?
          </h3>
          <p className={`${dynastyTheme.classes.text.neutralLight} mb-6`}>
            Try interacting with each mockup above. Which one feels best for your use case?
          </p>
          <div className="flex justify-center gap-4">
            <button className={dynastyTheme.utils.getComponent('button', 'primary', 'lg')}>
              I Choose Option {activeOption}
            </button>
          </div>
          <p className={`text-sm ${dynastyTheme.classes.text.neutralLighter} mt-4`}>
            Note: All options will have identical functionality, just different layouts
          </p>
        </div>
      </div>
    </div>
  );
};

// Comparison Row Component
const ComparisonRow = ({ feature, a, b, c }) => {
  return (
    <tr className={`border-b ${dynastyTheme.classes.border.neutral}`}>
      <td className={`p-3 ${dynastyTheme.classes.text.neutralLight}`}>{feature}</td>
      <td className={`text-center p-3 ${dynastyTheme.classes.text.white}`}>{a}</td>
      <td className={`text-center p-3 ${dynastyTheme.classes.text.white}`}>{b}</td>
      <td className={`text-center p-3 ${dynastyTheme.classes.text.white}`}>{c}</td>
    </tr>
  );
};

export default TradeProposalMockupViewer;
